import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, RefreshCw, TrendingUp, TrendingDown, Minus, Zap, Loader2, Package, Calendar, AlertCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceArea } from "recharts";
import { DeferredChart } from "@/components/DeferredChart";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function ForecastingPage() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [skus, setSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => {
      const bs = r.data.filter((b) => !b.is_head_office);
      setBranches(bs);
      if (bs.length && !branchId) setBranchId(bs[0].id);
    }).catch(() => toast.error("Failed to load branches"));
    // eslint-disable-next-line
  }, []);

  const loadSkus = async () => {
    if (!branchId) return;
    setLoadingSkus(true);
    try {
      const res = await api.get(`/forecasting/top-skus/${branchId}?limit=20`);
      setSkus(res.data);
      if (res.data.length && !selectedSku) setSelectedSku(res.data[0]);
    } catch (e) { toast.error("Failed to load top SKUs"); }
    finally { setLoadingSkus(false); }
  };
  useEffect(() => { loadSkus(); /* eslint-disable-next-line */ }, [branchId]);

  useEffect(() => {
    if (!branchId || !selectedSku) { setForecast(null); return; }
    api.get(`/forecasting/results/${branchId}/${selectedSku.product_id}`)
      .then((r) => setForecast(r.data))
      .catch(() => setForecast(null));
  }, [branchId, selectedSku]);

  const runOne = async (sku) => {
    setRunningId(sku.product_id);
    try {
      const res = await api.post(`/forecasting/run/${branchId}/${sku.product_id}`);
      setForecast(res.data);
      setSelectedSku(sku);
      toast.success(`${sku.sku} forecast refreshed`);
      await loadSkus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forecast failed");
    } finally {
      setRunningId(null);
    }
  };

  const runAll = async () => {
    setBatchRunning(true);
    try {
      const res = await api.post(`/forecasting/run-all/${branchId}`);
      toast.success(res.data.message || "Batch queued");
      setTimeout(loadSkus, 5000);
      setTimeout(loadSkus, 20000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally { setBatchRunning(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="forecasting-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="uc-label">AI Analytics</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C1810] mt-1 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-[#C05621]" /> Demand Forecasting
          </h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Powered by <span className="font-medium">Claude Sonnet 4.5</span>. Analyses 60 days of history per SKU and forecasts the next 7/14/30 days with a reorder recommendation.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#8B7355] mb-1">Branch</div>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="w-[220px]" data-testid="forecast-branch"><SelectValue placeholder="Choose branch" /></SelectTrigger>
              <SelectContent className="bg-white">
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={runAll} disabled={batchRunning || !branchId} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="forecast-batch-run">
            {batchRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
            Refresh all top 20
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card className="border-[#EAE5D9] h-fit">
          <CardHeader>
            <CardTitle className="font-[Outfit] text-lg flex items-center justify-between">
              <span>Top 20 SKUs · 60d</span>
              <button onClick={loadSkus} className="text-xs text-[#C05621] hover:underline"><RefreshCw className="w-3 h-3 inline" /></button>
            </CardTitle>
            <CardDescription>Selected by 60-day sales volume at this branch.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loadingSkus ? (
              <div className="p-8 text-center text-[#8B7355]"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>
            ) : skus.length === 0 ? (
              <div className="p-8 text-center text-[#8B7355] text-sm">No sales history yet at this branch.</div>
            ) : (
              <div className="divide-y divide-[#EAE5D9] max-h-[700px] overflow-y-auto" data-testid="forecast-sku-list">
                {skus.map((s) => (
                  <button
                    key={s.product_id}
                    onClick={() => setSelectedSku(s)}
                    className={`w-full text-left px-4 py-3 hover:bg-[#FAF6EE] flex items-center gap-3 ${selectedSku?.product_id === s.product_id ? "bg-[#FFF8F1]" : ""}`}
                    data-testid={`forecast-sku-${s.product_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{s.name}</div>
                      <div className="text-[10px] text-[#8B7355] font-mono truncate">{s.sku}</div>
                      <div className="text-[10px] text-[#8B7355] mt-0.5">
                        {s.units_sold_60d} units · {s.order_count_60d} orders
                        {s.last_forecast_next_7d != null && ` · next 7d: ${s.last_forecast_next_7d}`}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); runOne(s); }}
                      disabled={runningId === s.product_id}
                      className="text-xs px-2 py-1 rounded border border-[#EAE5D9] hover:bg-white disabled:opacity-50"
                      data-testid={`forecast-run-${s.product_id}`}
                    >
                      {runningId === s.product_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    </button>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div>
          {!selectedSku ? (
            <Card className="border-[#EAE5D9]">
              <CardContent className="p-16 text-center text-[#8B7355]">
                <Package className="w-10 h-10 mx-auto mb-4 opacity-40" />
                Select a SKU from the list to view its forecast.
              </CardContent>
            </Card>
          ) : (
            <ForecastPanel branchId={branchId} sku={selectedSku} forecast={forecast} onRerun={() => runOne(selectedSku)} runningId={runningId} />
          )}
        </div>
      </div>
    </div>
  );
}

function ForecastPanel({ branchId, sku, forecast, onRerun, runningId }) {
  const isRunning = runningId === sku.product_id;
  const f = forecast?.forecast || null;
  const daily7 = f?.daily_expected_next_7d || [];

  const trendIcon = useMemo(() => {
    const t = f?.history_summary?.trend;
    if (t === "up") return <TrendingUp className="w-3 h-3 text-emerald-600" />;
    if (t === "down") return <TrendingDown className="w-3 h-3 text-red-600" />;
    return <Minus className="w-3 h-3 text-[#8B7355]" />;
  }, [f]);

  // Combine last-30d actuals + next-7d forecast into one chart
  const chartData = useMemo(() => {
    const hist = (forecast?.history || []).slice(-30).map((h) => ({ date: h.date, actual: h.units_sold, forecast: null }));
    const fut = daily7.map((d) => ({ date: d.date, actual: null, forecast: d.expected_units }));
    return [...hist, ...fut];
  }, [forecast, daily7]);

  const confidenceCls = f?.confidence === "high" ? "bg-emerald-100 text-emerald-800"
    : f?.confidence === "medium" ? "bg-amber-100 text-amber-800"
    : "bg-red-100 text-red-800";

  return (
    <Card className="border-[#EAE5D9]" data-testid="forecast-detail">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="uc-label">{sku.sku}</div>
            <CardTitle className="font-[Outfit] text-2xl">{sku.name}</CardTitle>
            {forecast && (
              <div className="text-xs text-[#8B7355] mt-1">
                Generated {new Date(forecast.generated_at).toLocaleString("en-IN")} · window {forecast.window_days}d · {forecast.sales_count} orders · model {forecast.model}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {f?.confidence && <Badge className={`${confidenceCls} border-0`}>Confidence · {f.confidence}</Badge>}
            <Button onClick={onRerun} disabled={isRunning} variant="outline" data-testid="forecast-rerun">
              {isRunning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Regenerate
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {!forecast ? (
          <div className="p-10 text-center text-[#8B7355]">
            No forecast yet. Click <b>Regenerate</b> to compute.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <ForecastStat label="Next 7 days" range={f.forecast.next_7d} unit={forecast.unit} />
              <ForecastStat label="Next 14 days" range={f.forecast.next_14d} unit={forecast.unit} />
              <ForecastStat label="Next 30 days" range={f.forecast.next_30d} unit={forecast.unit} />
            </div>

            <div className="rounded-lg border border-[#EAE5D9] bg-white p-3">
              <div className="uc-label mb-2 text-[#8B7355]">History (30d) · Forecast (next 7d)</div>
              <div className="h-64">
                <DeferredChart height="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ left: 5, right: 15, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAE5D9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip labelStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="actual" stroke="#2C1810" strokeWidth={2} dot={{ r: 2 }} name="Actual" />
                      <Line type="monotone" dataKey="forecast" stroke="#C05621" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} name="Forecast" />
                    </LineChart>
                  </ResponsiveContainer>
                </DeferredChart>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-lg bg-[#FAF6EE] p-4 border border-[#EAE5D9]">
                <div className="uc-label text-[#8B7355] mb-2 flex items-center gap-2">
                  <TrendingUp className="w-3 h-3" /> Pattern
                </div>
                <div className="text-sm space-y-1">
                  <Row label="Days with sales" value={`${f.history_summary?.days_with_sales} / ${forecast.window_days}`} />
                  <Row label="Avg daily units" value={f.history_summary?.avg_daily_units} />
                  <Row label="Peak weekday" value={f.history_summary?.peak_weekday} />
                  <Row label="Trend" value={<span className="flex items-center gap-1 justify-end">{trendIcon}{f.history_summary?.trend}</span>} />
                  <Row label="Variability (CoV)" value={f.history_summary?.coefficient_of_variation} />
                </div>
                <div className="mt-3 text-xs text-[#5C4033] italic border-l-2 border-[#C05621] pl-2">
                  {f.reasoning}
                </div>
              </div>

              <div className="rounded-lg bg-white p-4 border-2 border-[#C05621]">
                <div className="uc-label text-[#C05621] mb-2 flex items-center gap-2">
                  <AlertCircle className="w-3 h-3" /> Reorder recommendation
                </div>
                <div className="text-3xl font-[Outfit] font-bold text-[#2C1810]">
                  {f.recommendation?.reorder_units} <span className="text-lg font-normal text-[#8B7355]">{forecast.unit}</span>
                </div>
                <div className="text-sm mt-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-[#8B7355]" />
                  by <b>{f.recommendation?.reorder_by_date}</b>
                </div>
                <div className="mt-3 text-sm text-[#5C4033]">
                  {f.recommendation?.notes}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ForecastStat({ label, range, unit }) {
  return (
    <div className="rounded-lg border border-[#EAE5D9] bg-white p-4">
      <div className="uc-label text-[#8B7355]">{label}</div>
      <div className="mt-1 text-3xl font-[Outfit] font-bold text-[#C05621]">{range?.expected ?? "—"} <span className="text-sm text-[#8B7355] font-normal">{unit}</span></div>
      <div className="text-[10px] text-[#8B7355] mt-1">range {range?.low}–{range?.high}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-[#8B7355]">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
